import React from "react";
import { FormProvider } from "react-hook-form";
import { Link as RouterLink } from "react-router";

import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import Button from "components/Button";
import Divider from "components/Divider";
import InfoBox from "components/InfoBox";
import StyledFormInput from "components/FormInput/FormInput";
import FormNumericInput from "components/FormInput/FormNumericInput";

import Banks from "./Banks";
import AmountBox from "./AmountBox";
import BottomBanner from "./BottomBanner";
import MobileTopBackground from "./MobileTopBackground";
import AccountInfoCollapsible from "./AccountInfoCollapsible";
import PageHeading, { PageHeadingVariant } from "./PageHeading";

import { FormValues } from "./IbanPaymentForm/validation";
import { useIbanInvoiceForm } from "./IbanPaymentForm/useIbanInvoiceForm";

import { IbanInvoiceResponseDto } from "api";

import { routes } from "routes/routes";
import { Bank } from "shared/constants/banks";
import { getPriceFormat } from "shared/functions";
import { substitutePaymentPurposeTokens } from "shared/functions/ibanQrHelpers";

import { styles } from "./styles";

interface IbanMobileContentProps {
  iban: IbanInvoiceResponseDto | null;
  preferredBank?: string;
  handlePayment?: (values?: FormValues, bank?: Bank) => Promise<void>;
}

const IbanMobileContent: React.FC<IbanMobileContentProps> = (props) => {
  const { iban, preferredBank, handlePayment } = props;

  const { amount = 0, paymentPurpose = "", clientNotes = "" } = iban || {};

  const {
    methods,
    showForm,
    showAmountInput,
    showPayerFullName,
    showOrderNumber,
    isLoading,
    onSubmit,
  } = useIbanInvoiceForm({
    iban,
    handlePayment,
  });

  const formAmount = methods.watch("amount");
  const payerFullName = methods.watch("payerFullName");
  const orderNumber = methods.watch("orderNumber");

  const substitutedPaymentPurpose = substitutePaymentPurposeTokens(
    paymentPurpose,
    payerFullName,
    orderNumber,
  );

  const ibanWithSubstitutedPurpose = iban
    ? { ...iban, paymentPurpose: substitutedPaymentPurpose }
    : null;

  const onBankClick = (bank: Bank) => {
    methods.handleSubmit((values) => {
      handlePayment && handlePayment(values, bank);
    })();
  };

  return (
    <>
      <MobileTopBackground />
      <PageHeading
        variant={PageHeadingVariant.LIGHT}
        isPreview={!handlePayment}
      />
      <AccountInfoCollapsible
        iban={ibanWithSubstitutedPurpose}
        isPreview={!handlePayment}
      />

      <Paper elevation={5} sx={styles.paper}>
        <Stack gap={2}>
          {!showAmountInput && (
            <AmountBox amount={amount} note={substitutedPaymentPurpose} />
          )}
          {showForm ? (
            <FormProvider {...methods}>
              <Stack gap={2}>
                {showAmountInput && (
                  <>
                    <FormNumericInput
                      testId="preview-amount"
                      sx={{ mb: 0 }}
                      title="Сума до сплати:"
                      name="amount"
                      showRequired
                      fullWidth
                    />
                    <Typography
                      variant="subtitle2"
                      color="grey800"
                      sx={{
                        textAlign: "left",
                        mt: -1,
                        overflowWrap: "break-word",
                      }}
                    >
                      {substitutedPaymentPurpose}
                    </Typography>
                  </>
                )}

                {showPayerFullName && (
                  <StyledFormInput
                    testId="payerFullName"
                    title="Ім'я та прізвище"
                    name="payerFullName"
                    showRequired
                    fullWidth
                    slotProps={{ input: { inputProps: { maxLength: 40 } } }}
                  />
                )}
                {showOrderNumber && (
                  <StyledFormInput
                    testId="orderNumber"
                    title="Номер замовлення"
                    name="orderNumber"
                    showRequired
                    fullWidth
                    slotProps={{ input: { inputProps: { maxLength: 15 } } }}
                  />
                )}

                {clientNotes && (
                  <InfoBox title="Зверніть увагу!" content={clientNotes} />
                )}

                <Stack>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={onSubmit}
                    loading={isLoading}
                  >
                    Оплатити {getPriceFormat(Number(formAmount) || 0)} ₴
                  </Button>
                  {showPayerFullName && (
                    <Typography
                      variant="subtitle2"
                      color="grey800"
                      sx={{ mt: 1, mb: 1, textAlign: "center" }}
                    >
                      Продовжуючи, я погоджуюсь з умовами{" "}
                      <Link
                        component={RouterLink}
                        variant="subtitle2"
                        to={routes.termsOfService}
                      >
                        Політики конфіденційності
                      </Link>
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </FormProvider>
          ) : (
            <>
              {clientNotes && (
                <InfoBox title="Зверніть увагу!" content={clientNotes} />
              )}
              <Button
                variant="contained"
                onClick={onSubmit}
                fullWidth
                loading={isLoading}
              >
                Оплатити {getPriceFormat(Number(iban?.amount) || 0)} ₴
              </Button>
            </>
          )}

          <Typography
            variant="subtitle2"
            color="grey700"
            sx={{ textAlign: "center" }}
          >
            Відкриється ваш основний банківський додаток.
          </Typography>

          <Divider text="або оберіть зі списку" />
          <Banks
            iban={ibanWithSubstitutedPurpose}
            preferredBankName={preferredBank}
            onBankClick={onBankClick}
            isPreview={!handlePayment}
          />
        </Stack>
      </Paper>

      <BottomBanner isPreview={!handlePayment} />
    </>
  );
};

export default IbanMobileContent;
